import { useTheme } from "styled-components";
import type { TypeTheme } from "@sproutsocial/seeds-react-theme";
import { ViewBoxes } from "@sproutsocial/seeds-partner-logos";
import { PartnerLogoSizes } from "./PartnerLogoTypes";
import type { TypePartnerLogoProps } from "./PartnerLogoTypes";

const whichLogo = (
  {
    backgroundType,
    logoType,
    partnerName,
  }: Pick<TypePartnerLogoProps, "backgroundType" | "logoType" | "partnerName">,
  mode: TypeTheme["mode"]
) => {
  const logo =
    logoType && logoType !== "symbol"
      ? partnerName + "-" + logoType
      : partnerName;
  if (backgroundType && backgroundType === "dark") {
    return `${logo}-dark`;
  } else if (backgroundType && backgroundType === "light") {
    return logo;
  } else if (mode === "dark") {
    return `${logo}-dark`;
  } else {
    return logo;
  }
};

/**
 * Shared compute for both render paths (Tailwind + styled-components).
 *
 * Calls useTheme() so dark-logo selection works identically on both paths, and
 * returns the resolved logo id, viewBox, aria props, and the size values used to
 * drive sizing. Sizing precedence is preserved verbatim from the original
 * stylesForSize block: a logoSize lookup wins over the height prop, and the svg
 * width is the logoSize lookup or empty (which sizes the svg from the viewBox).
 */
export const usePartnerLogoModel = ({
  partnerName,
  backgroundType,
  logoType = "symbol",
  height,
  width,
  size,
  "aria-label": ariaLabel,
}: TypePartnerLogoProps) => {
  const { mode } = useTheme() as TypeTheme;

  const logo = whichLogo({ backgroundType, logoType, partnerName }, mode);
  const logoViewBox = ViewBoxes[logo as keyof typeof ViewBoxes];
  const viewBoxCoordinates = logoViewBox?.split(" ") || [];
  const defaultSize = logoType === "symbol" ? "small" : undefined;
  const logoSizeKey = size ?? defaultSize;
  const logoSizePx = logoSizeKey ? PartnerLogoSizes[logoSizeKey] : undefined;

  // we add pixels here because view box coordinates are numbers
  const resolvedHeight = height ? height : viewBoxCoordinates[3] + "px";
  const resolvedWidth = width ? width : viewBoxCoordinates[2] + "px";
  // container height + line-height + svg height: logoSize wins over height
  const sizeValue = logoSizePx ? logoSizePx : resolvedHeight;
  // svg width: logoSize or empty (empty => sizes from viewBox aspect ratio)
  const svgWidth = logoSizePx ? logoSizePx : "";

  const hasAriaLabel = !!ariaLabel;
  const containerAriaProps = hasAriaLabel ? {} : { "aria-hidden": true };
  const svgAriaProps = hasAriaLabel
    ? { role: "img", "aria-label": ariaLabel }
    : {};

  return {
    logo,
    logoViewBox,
    logoSizeKey,
    resolvedHeight,
    resolvedWidth,
    sizeValue,
    svgWidth,
    containerAriaProps,
    svgAriaProps,
  };
};
